import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

const authHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}` };
};

/** Canais com login da Meta (botão e retorno próprios). O WhatsApp conecta pelo Embedded Signup (WhatsAppSignupButton). */
export type MetaChannel = 'messenger' | 'instagram';

/** Canal de uma conta no status: os dois acima mais o WhatsApp oficial (Cloud API direta). */
export type MetaAccountChannel = MetaChannel | 'whatsapp';

/** active · needs_reconnect (a Meta recusou o token) · webhook_unsubscribed (a Meta desligou o recebimento) · policy_warning (aviso de política) */
export type MetaAccountState = 'active' | 'needs_reconnect' | 'webhook_unsubscribed' | 'policy_warning' | string;

export interface MetaAccountStatus {
  channel: MetaAccountChannel;
  accountId: string;
  pageId: string | null;
  name: string | null;
  username: string | null;
  status: MetaAccountState;
  lastWebhookAt: string | null;
  lastError: string | null;
  /** Eventos recebidos que não puderam ser processados depois de todas as tentativas. */
  failedEvents: number;
  /** system_user · user_long_lived · instagram_user */
  tokenKind?: string | null;
  /** Validade do token da conta (nula = não expira; no Instagram depende da configuração do login unificado na Meta). */
  tokenExpiresAt?: string | null;
  /** true = o token não expira (Page token, usuário de sistema). */
  tokenNeverExpires?: boolean;
  // WhatsApp oficial (nunca vem token nem PIN)
  /** Número como a Meta mostra. */
  displayPhone?: string | null;
  /** GREEN · YELLOW · RED · UNKNOWN */
  qualityRating?: string | null;
  /** Coexistência: o número também está no WhatsApp Business app. */
  isOnBizApp?: boolean;
  /** Falta forma de pagamento na conta (o envio não é bloqueado). */
  paymentWarning?: string | null;
  /** O acesso de 60 dias vence em até 7 dias (ou já venceu): reconectar pelo botão renova. */
  tokenExpiryWarning?: string | null;
  /** requested · importing · completed · declined · failed */
  historySyncStatus?: string | null;
  historySyncRequestedAt?: string | null;
  /** Andamento da importação do histórico, de 0 a 100. */
  historySyncProgress?: number | null;
  /** Texto pronto quando a empresa recusou compartilhar o histórico no celular. */
  historySyncMessage?: string | null;
}

/** O que o navegador precisa para abrir o Embedded Signup (nada aqui é segredo). */
export interface WhatsAppSignupConfig {
  appId: string;
  configId: string;
  graphApiVersion: string;
}

/** Um canal configurado neste ambiente? O botão dele só aparece quando sim. */
export interface MetaChannelConfig {
  configured: boolean;
  missing: string[];
}

export interface MetaConnectionStatus {
  /** false = faltam chaves da Meta neste ambiente (nenhum botão de conectar). */
  configured: boolean;
  reason: string | null;
  missing: string[];
  accounts: MetaAccountStatus[];
  /** Configuração por canal (backends antigos não mandam: vale `configured`). */
  messenger?: MetaChannelConfig;
  instagram?: MetaChannelConfig;
  whatsApp?: MetaChannelConfig;
  /** Só vem com o WhatsApp configurado. */
  whatsAppSignup?: WhatsAppSignupConfig | null;
}

/** Página do Facebook oferecida no seletor (só Messenger: o Instagram conecta pelo próprio login, sem Página). */
export interface MetaSelectablePage {
  pageId: string;
  pageName: string;
  messengerConnected: boolean;
}

export interface MetaSaveResult {
  status: MetaConnectionStatus;
  errors: Array<{ pageId: string; message: string }>;
}

export const NOT_CONFIGURED_MESSAGE = 'Integração com a Meta ainda não configurada neste ambiente';

export const getMetaConnectionStatus = async (sectorId: number): Promise<MetaConnectionStatus> => {
  const response = await axios.get<MetaConnectionStatus>(`${API_URL}/MetaConnection/status/${sectorId}`, {
    headers: authHeaders(),
  });
  return response.data;
};

/** `instagram` abre o login unificado do Instagram (Instagram ou Facebook, sem Página); `messenger`, o Facebook Login da Página. */
export const fetchMetaAuthorizeUrlAndRedirect = async (
  sectorId: number,
  channel: MetaChannel = 'messenger',
): Promise<void> => {
  const response = await axios.get<{ url: string }>(
    `${API_URL}/MetaConnection/oauth/authorize-url?sectorId=${sectorId}&channel=${channel}`,
    { headers: authHeaders() },
  );
  const url = response.data?.url;
  if (!url) throw new Error('No redirect URL received');
  window.location.href = url;
};

/**
 * Conclui o login da Meta: o retorno (anônimo) só devolve `code` e `state` para a tela, e é este POST autenticado que
 * confere se quem voltou é quem começou antes de trocar o código.
 */
export interface MetaLoginResult {
  sectorId: number;
  provider?: 'facebook' | 'instagram';
  /** false = já conectou (Instagram); true/ausente = falta escolher as Páginas (Messenger). */
  needsSelection?: boolean;
}

export const completeMetaLogin = async (code: string, state: string): Promise<MetaLoginResult> => {
  const response = await axios.post<MetaLoginResult>(
    `${API_URL}/MetaConnection/oauth/complete`,
    { code, state },
    { headers: authHeaders() },
  );
  return response.data;
};

export const listMetaPages = async (sectorId: number): Promise<MetaSelectablePage[]> => {
  const response = await axios.get<MetaSelectablePage[]>(`${API_URL}/MetaConnection/accounts/${sectorId}`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const saveMetaSelection = async (sectorId: number, pageIds: string[]): Promise<MetaSaveResult> => {
  const response = await axios.post<MetaSaveResult>(
    `${API_URL}/MetaConnection/accounts/${sectorId}`,
    { pageIds },
    { headers: authHeaders() },
  );
  return response.data;
};

/** Sem `channel`, desconecta todos (Messenger, Instagram e WhatsApp); com ele, só aquele canal. */
export const disconnectMeta = async (sectorId: number, channel?: MetaAccountChannel): Promise<MetaConnectionStatus> => {
  const response = await axios.post<MetaConnectionStatus>(
    `${API_URL}/MetaConnection/disconnect/${sectorId}${channel ? `?channel=${channel}` : ''}`,
    {},
    { headers: authHeaders() },
  );
  return response.data;
};

export interface WhatsAppCompleteResult {
  sectorId: number;
  phoneNumberId: string;
  /** Coexistência: o número também está no WhatsApp Business app. */
  isOnBizApp: boolean;
  /** Pediu agora a importação de contatos e conversas. */
  syncRequested: boolean;
  /** O setor tinha Spark ativo e ele foi desligado. */
  sparkSwitchedOff: boolean;
  paymentMethodMissing: boolean;
  status: MetaConnectionStatus;
}

/**
 * Conclui o Embedded Signup do WhatsApp: manda o `code` (vale 30 segundos) e o session info que o navegador recebeu da Meta.
 * O servidor troca o code, lê o número, inscreve, registra e liga o setor ao WhatsApp oficial.
 */
export const completeWhatsAppConnection = async (
  sectorId: number,
  code: string,
  sessionInfo: Record<string, unknown>,
): Promise<WhatsAppCompleteResult> => {
  const response = await axios.post<WhatsAppCompleteResult>(
    `${API_URL}/MetaConnection/whatsapp/complete`,
    { sectorId, code, sessionInfo },
    { headers: authHeaders() },
  );
  return response.data;
};

/** Mensagem em português que o backend devolveu no erro (ou o texto de reserva). */
export const metaRequestErrorMessage = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
};

/** Texto em português para o código `meta_error` que o backend devolve na URL do retorno do login. */
export const metaErrorMessage = (code: string): string => {
  switch (code) {
    case 'access_denied':
      return 'Você cancelou a conexão com a Meta. Nada foi conectado.';
    case 'state_invalid':
      return 'O link de conexão venceu ou é inválido. Comece de novo em Conectar.';
    case 'state_used':
      return 'Este link de conexão já foi usado. Comece de novo em Conectar.';
    case 'state_user_mismatch':
      return 'Este link de conexão foi gerado por outra pessoa. Comece de novo em Conectar, com a sua conta.';
    case 'exchange_failed':
      return 'A Meta não confirmou o login. Tente de novo.';
    case 'no_instagram_account':
      return 'Nenhuma conta profissional do Instagram foi compartilhada no login. Conecte de novo e escolha a conta.';
    case 'already_connected':
      return 'Esta conta já está conectada a outro setor do Mends.';
    case 'subscribe_failed':
      return 'Não foi possível ligar o recebimento de mensagens. Tente de novo.';
    case 'not_configured':
      return `${NOT_CONFIGURED_MESSAGE}.`;
    case 'key_missing':
      return 'A conexão com a Meta está indisponível: falta uma configuração de segurança neste ambiente.';
    default:
      return 'Não foi possível concluir a conexão com a Meta. Tente de novo.';
  }
};

/** Rótulo do estado de uma conta, como aparece em Configurações. */
export const metaAccountStateLabel = (state: MetaAccountState): string => {
  switch (state) {
    case 'pending':
      return 'Conectando';
    case 'active':
      return 'Online';
    case 'needs_reconnect':
      return 'Precisa reconectar';
    case 'webhook_unsubscribed':
      return 'Recebimento desligado pela Meta';
    case 'policy_warning':
      return 'Aviso da Meta';
    default:
      return 'Indisponível';
  }
};

/**
 * Bolinha de conexão do menu para setor com WhatsApp oficial (Cloud API): "connected" só com todas as contas ativas.
 * Null quando o setor não tem conta da Cloud API (vale o status do caminho antigo).
 */
export const cloudWhatsAppConnectionState = (
  accounts: Pick<MetaAccountStatus, 'channel' | 'status'>[] | undefined | null,
): 'connected' | 'disconnected' | null => {
  const mine = (accounts || []).filter((a) => a.channel === 'whatsapp');
  if (mine.length === 0) return null;
  return mine.every((a) => a.status === 'active') ? 'connected' : 'disconnected';
};
